import React, { FC, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useSelector } from 'react-redux';
import type { Department } from '../../config/businesses';
import { RootState } from '../../redux/store';
import { useRegistrationContext } from '../../contexts/RegistrationContext';

interface DepartmentSelectionFormProps {
  onNext: () => void;
  onBack: () => void;
}

const SEARCH_THRESHOLD = 10;

const buildSearchRegex = (input: string): RegExp => {
  try {
    return new RegExp(input, 'i');
  } catch {
    const escaped = input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(escaped, 'i');
  }
};

export const DepartmentSelectionForm: FC<DepartmentSelectionFormProps> = ({
  onNext,
  onBack,
}) => {
  const [department, setDepartment] = useState<string>('');
  const { updateField } = useRegistrationContext();

  // ✅ OBTENER DEPARTAMENTOS DE LA EMPRESA SELECCIONADA
  const { departments = [] } = useSelector(
    (state: RootState) => state.business
  );

  const showSearch = departments.length > SEARCH_THRESHOLD;

  const applyDepartment = (activationCode: string) => {
    setDepartment(activationCode);
    // ✅ ACTUALIZAR EL activationCode EN EL FORMULARIO DE REGISTRO
    updateField('activationCode', activationCode);
  };

  const handleDepartmentChange = (event: SelectChangeEvent) => {
    applyDepartment(event.target.value);
  };

  const selectedOption =
    departments.find(d => d.activationCode === department) ?? null;

  return (
    <Box sx={{ paddingX: '5%', paddingY: '5vh' }}>
      <Button
        onClick={onBack}
        size="small"
        sx={{ marginY: '2%', fontWeight: 'bold' }}
        variant="text"
      >
        {'← Atras'}
      </Button>
      {showSearch ? (
        <Autocomplete<Department>
          options={departments}
          value={selectedOption}
          onChange={(_, option) =>
            applyDepartment(option?.activationCode ?? '')
          }
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(a, b) => a.activationCode === b.activationCode}
          filterOptions={(options, { inputValue }) => {
            const term = inputValue.trim();
            if (!term) return options;
            const regex = buildSearchRegex(term);
            return options.filter(option => regex.test(option.name));
          }}
          noOptionsText="Sin resultados"
          sx={{ width: '100%', marginTop: '10vh' }}
          ListboxProps={{ style: { maxHeight: 320 } }}
          renderInput={params => (
            <TextField
              {...params}
              label="Departamento"
              placeholder="Buscar departamento"
              variant="outlined"
            />
          )}
        />
      ) : (
        <FormControl sx={{ width: '100%', marginTop: '10vh' }}>
          <InputLabel id="department-select-label">Departamento</InputLabel>
          <Select
            value={department}
            onChange={handleDepartmentChange}
            labelId="department-select-label"
            id="department-select"
            label="Departamento"
            variant="outlined"
            sx={{ width: '100%' }}
          >
            {/* ✅ DEPARTAMENTOS DINÁMICOS DE LA EMPRESA SELECCIONADA */}
            {departments.map(dept => (
              <MenuItem key={dept.name} value={dept.activationCode}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={12}
      >
        <Button
          disabled={!department}
          onClick={onNext}
          size="large"
          sx={{ marginY: '5vh', fontWeight: 'bold', borderRadius: '50px' }}
          variant="contained"
        >
          {'Siguiente →'}
        </Button>
      </Stack>
    </Box>
  );
};
